import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ActiveCartFacade, CART_NORMALIZER, MultiCartFacade } from '@spartacus/cart/base/root';
import {
  ConverterService,
  getLastValueSync,
  GlobalMessageService,
  GlobalMessageType,
  LoggerService,
  OCC_USER_ID_ANONYMOUS,
  OccEndpointsService,
  RoutingService,
  UserIdService
} from '@spartacus/core';
import { OrderActions, OrderSelectors, StateWithOrder } from '@spartacus/order/core';
import { Order } from '@spartacus/order/root';
import { Observable, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { getHttpErrorMessage } from '../../../../../../custom/util/standardize-http-error';
import { OrdersFilter } from '../../../../model/order-filters.model';
import { CustomLoadOrderDetails, CustomLoadUserOrders, LoadUserOrdersStatus } from '../../store/actions/custom-user-order.action';
import { getOrderStatusList, StateWithOrderStatusList } from '../../store/selectors/custom-user-order.selector';

@Injectable({
  providedIn: 'root'
})
export class CustomUserOrderService implements OnDestroy {
  protected logger = inject(LoggerService);
  protected subscriptions: Subscription = new Subscription();
  userId: string = '';

  constructor(
    protected store: Store<StateWithOrder>,
    protected userIdService: UserIdService,
    protected routingService: RoutingService,
    protected orderListStore: Store<StateWithOrderStatusList>,
    protected occEndpoints: OccEndpointsService,
    protected http: HttpClient,
    protected activeCartService: ActiveCartFacade,
    protected converterService: ConverterService,
    protected multiCartService: MultiCartFacade,
    protected routing: RoutingService,
    protected globalMessageService: GlobalMessageService
  ) {}

  /**
   * Retrieves an order list
   * @param pageSize page size
   * @param currentPage current page
   * @param sort sort
   * @param ordersFilter custom order filters
   */
  loadOrderList(pageSize: number, currentPage?: number, sort?: string, ordersFilter?: OrdersFilter): void {
    this.subscriptions.add(
      this.userIdService.takeUserId().subscribe((userId) => {
        if (userId !== OCC_USER_ID_ANONYMOUS) {
          let replenishmentOrderCode: string;

          this.routingService
            .getRouterState()
            .pipe(take(1))
            .subscribe((data) => {
              replenishmentOrderCode = data?.state?.params?.replenishmentOrderCode;
              this.store.dispatch(
                new CustomLoadUserOrders({
                  userId,
                  pageSize,
                  currentPage,
                  sort,
                  replenishmentOrderCode,
                  ordersFilter
                })
              );
            })
            .unsubscribe();
        }
      })
    );
  }

  /**
   * Retrieves an order list
   */
  loadStatusList(): void {
    this.store.dispatch(new LoadUserOrdersStatus());
  }

  /**
   * Retrieves an order list
   */
  getStatusList(): Observable<any> {
    return this.orderListStore.pipe(select(getOrderStatusList));
  }

  /**
   * Retrieves order's details
   *
   * @param orderCode an order code
   */
  loadOrderDetails(orderCode: string): void {
    this.subscriptions.add(
      this.userIdService.takeUserId().subscribe((userId) => {
        this.store.dispatch(
          new CustomLoadOrderDetails({
            userId,
            orderCode
          })
        );
      })
    );
  }

  loadRepeatOrder(userId: string, cartId: string, orderCode: string): void {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const url = this.occEndpoints.buildUrl('orderRestoration', {
      urlParams: {
        userId,
        cartId,
        code: orderCode
      }
    });
    const params = new HttpParams().set('userId', userId).set('code', orderCode).set('cartId', cartId);
    this.subscriptions.add(
      this.http.post<any>(url, params, { headers }).subscribe({
        next: (data) => {
          this.converterService.pipeable(CART_NORMALIZER);
          this.multiCartService.loadCart({
            userId,
            cartId,
            extraData: {
              active: true
            }
          });
          this.routing.go({ cxRoute: 'cart' });
        },
        error: (error) => {
          const httpErrorMessage = getHttpErrorMessage(error, this.logger);
          this.globalMessageService.add(httpErrorMessage, GlobalMessageType.MSG_TYPE_ERROR);
        }
      })
    );
  }

  /**
   * Repeat order
   *
   * @param orderCode an order code
   */
  repeatOrder(orderCode: string): void {
    let cartId = getLastValueSync(this.activeCartService.getActiveCartId());
    this.subscriptions.add(this.userIdService.takeUserId().subscribe((userID) => (this.userId = userID)));

    /* Check if the cart exist*/
    if (cartId) {
      this.loadRepeatOrder(this.userId, cartId, orderCode);
    } else {
      /* If the cart doesn't exists create a new cart and get its id.*/
      this.subscriptions.add(
        this.multiCartService
          .createCart({
            userId: this.userId,
            extraData: {
              active: true
            }
          })
          .pipe(take(1))
          .subscribe((response) => {
            if (response.code) {
              cartId = getLastValueSync(this.activeCartService.getActiveCartId()) || '';
              this.loadRepeatOrder(this.userId, cartId, orderCode);
            }
          })
      );
    }
  }

  clearOrderDetails(): void {
    this.store.dispatch(new OrderActions.ClearOrderDetails());
  }

  getOrderDetails(): Observable<Order> {
    return this.store.pipe(select(OrderSelectors.getOrderDetails));
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
